// src/components/hero/ProfileGrid.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';

/* ──────────────────────────────────────────────────────────
   Personas: name, city, title, and a standalone emoji
─────────────────────────────────────────────────────────── */
export const PERSONAS = [
  { firstName: 'Sarah',    city: 'London',    title: 'Tennis Trainer',     emoji: '🎾' },
  { firstName: 'James',    city: 'New York',  title: 'Yoga Guide',         emoji: '🧘' },
  { firstName: 'Elena',    city: 'Barcelona', title: 'Padel Lover',        emoji: '🎾' },
  { firstName: 'Michael',  city: 'Berlin',    title: 'Urban Runner',       emoji: '🏃‍♂️' },
  { firstName: 'Victoria', city: 'Singapore', title: 'Pilates Instructor', emoji: '🤸' },
  { firstName: 'Sandra',   city: 'Paris',     title: 'Golf Enthusiast',    emoji: '⛳' },
  { firstName: 'David',    city: 'Sydney',    title: 'Wellness Trainer',   emoji: '🌿' }
];

interface ProfileGridProps {
  profiles: { image: string; name: string }[];
  onRequestAccess: () => void;
  className?: string;
}

const ProfileGrid: React.FC<ProfileGridProps> = ({
  profiles,
  onRequestAccess,
  className
}) => {
  const { isDarkMode } = useTheme();
  const [centerIndex, setCenterIndex] = useState(0);

  /* merge avatar images with persona data */
  const enrichedProfiles = useMemo(
    () => profiles.map((p, idx) => ({ ...p, ...PERSONAS[idx % PERSONAS.length] })),
    [profiles]
  );

  /* cycle through avatars every 2s */
  useEffect(() => {
    const id = setInterval(
      () => setCenterIndex(i => (i + 1) % profiles.length),
      2000
    );
    return () => clearInterval(id);
  }, [profiles.length]);

  return (
    <div className={cn('flex flex-col items-center', className)}>
      {/* Central avatar */}
      <div className="relative w-24 h-24 mb-6">
        <div className="absolute inset-0 rounded-full border-2 border-gold-400 ring-4 ring-gold-400/30">
          <motion.div
            className="absolute inset-0 rounded-full"
            initial={{ opacity: 0.5, scale: 1 }}
            animate={{ opacity: 0, scale: 1.6 }}
            transition={{ repeat: Infinity, duration: 1.5, ease: 'easeOut' }}
            style={{
              background: `radial-gradient(circle,${
                isDarkMode ? 'rgba(251,191,36,0.3)' : 'rgba(251,191,36,0.2)'
              } 0%,transparent 70%)`
            }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={centerIndex}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full rounded-full overflow-hidden"
          >
            <img
              src={enrichedProfiles[centerIndex].image}
              alt={enrichedProfiles[centerIndex].firstName}
              className="w-full h-full object-cover"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Active profile details */}
      <AnimatePresence mode="wait">
        <motion.div
          key={centerIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="text-center mb-6 h-24"
        >
          <p className="text-lg font-semibold !text-white">
            {enrichedProfiles[centerIndex].firstName}
          </p>

          <p className="text-4xl leading-none my-1 !text-white">
            {enrichedProfiles[centerIndex].emoji}
          </p>

          <p className="text-sm !text-gold-400">
            {enrichedProfiles[centerIndex].title}
          </p>

          <p className="text-xs mb-6 !text-white/70">
            {enrichedProfiles[centerIndex].city}
          </p>
        </motion.div>
      </AnimatePresence>

      {/* CTA – opens Apply Modal */}
      <motion.button
        onClick={onRequestAccess}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className={cn(
          'mt-0 px-6 py-3 rounded-full',
          'bg-gold-400 hover:bg-gold-500 text-dark-50',
          'font-medium shadow-lg hover:shadow-xl',
          'transform hover:scale-105 active:scale-95',
          'transition-all duration-300'
        )}
      >
        Join the Community
      </motion.button>
    </div>
  );
};

export default ProfileGrid;
